"use client"
import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button, type ButtonProps } from "@/components/ui/button"
import { useUndoQualityChangeMutation } from "@/hooks/mutations/conversation-quality"

import type { SuggestionKind } from "@/lib/conversation-quality/suggestions/schema"
import { useTranslations } from "next-intl"


/** Undo of one change, behind its confirm, which stays open until the undo settles. */
export function UndoChangeButton({
  suggestionId,
  kind,
  label,
  size,
  onUndone,
}: {
  suggestionId: string
  kind: SuggestionKind
  label: string
  size?: ButtonProps["size"]
  onUndone?: () => void | Promise<void>
}) {
  const { refusalNote } = useQualityChanges()

  const tUi = useTranslations("ConversationQuality.ConversationQualityUndoChangeButton")
  const tCommonUi = useTranslations("Common")

  const [confirming, setConfirming] = useState(false)
  const undo = useUndoQualityChangeMutation({
    onUndone: async () => {
      await onUndone?.()
      setConfirming(false)
    },
  })

  return (
    <>
      <Button type="button" variant="outline" size={size} onClick={() => setConfirming(true)}>
        <span>{label}</span>
      </Button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("undoThisChange")}>{tUi("undoThisChange")}</span></AlertDialogTitle>
            <AlertDialogDescription>{refusalNote(kind, "undo")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undo.isPending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              disabled={undo.isPending}
              onClick={(event) => {
                // Radix closes the dialog on this click; it stays open until the undo settles.
                event.preventDefault()
                undo.mutateAsync({ input: { suggestionId } }).catch(() => {})
              }}
            >
              <span key={tUi("undo")}>{tUi("undo")}</span></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
